import React from 'react';
import { useTransaction } from '../contexts/TransactionContext';
import { usePrice } from '../contexts/PriceContext';

const TransactionModal = () => {
  const { showModal, pendingTransaction, confirmTransaction, cancelTransaction } = useTransaction();
  const { ethPrice, formatUSD } = usePrice();

  if (!showModal || !pendingTransaction) return null;

  const formatValue = (value) => {
    try {
      if (typeof value === 'string' && value.startsWith('0x')) {
        return (parseInt(value, 16) / 1e18).toFixed(6);
      }
      if (typeof value === 'bigint') {
        return (Number(value) / 1e18).toFixed(6);
      }
      return value || '0';
    } catch (e) {
      return '0';
    }
  };

  const txValue = formatValue(pendingTransaction.value);
  const usdValue = (parseFloat(txValue) * (ethPrice || 0)).toFixed(2);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.95)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--card-bg)',
        padding: '30px',
        borderRadius: '15px',
        border: '2px solid var(--gold)',
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 0 50px rgba(255, 215, 0, 0.3)'
      }}>
        <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
          Confirm Transaction
        </h3>

        <div style={{
          background: 'var(--black)',
          padding: '20px',
          borderRadius: '10px',
          border: '1px solid var(--gold)',
          marginBottom: '25px'
        }}>
          {pendingTransaction.type === 'SET_TEXT_RECORD' ? (
            // ENS Text Record
            <>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>
                  Action:
                </div>
                <div style={{ color: 'var(--text-light)' }}>
                  Set ENS Text Record
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>
                  ENS Name:
                </div>
                <div style={{ color: 'var(--text-light)', fontFamily: 'monospace' }}>
                  {pendingTransaction.ensName || pendingTransaction.domain || 'Unknown'}
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>
                  Record Key:
                </div>
                <div style={{ color: 'var(--text-light)' }}>
                  {pendingTransaction.key || pendingTransaction.recordKey || 'Unknown'}
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>
                  New Value:
                </div>
                <div style={{ 
                  color: 'var(--text-light)', 
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  maxHeight: '100px',
                  overflowY: 'auto',
                  padding: '5px',
                  background: 'rgba(0,0,0,0.5)',
                  borderRadius: '4px'
                }}>
                  {pendingTransaction.value || pendingTransaction.textValue || 'Empty'}
                </div>
              </div>
            </>
          ) : (
            // ETH Transfer
            <>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>
                  To:
                </div>
                <div style={{ color: 'var(--text-light)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {pendingTransaction.to}
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>
                  Amount:
                </div>
                <div style={{ color: 'var(--text-light)', fontSize: '18px', fontWeight: 'bold' }}>
                  {txValue} ETH
                  {ethPrice > 0 && (
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '5px' }}>
                      ≈ {formatUSD(usdValue)}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>
              From:
            </div>
            <div style={{ color: 'var(--text-light)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {pendingTransaction.from || pendingTransaction.address || 'Unknown'}
            </div>
          </div>

          <div>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>
              Network:
            </div>
            <div style={{ color: 'var(--text-light)' }}>
              Ethereum Mainnet
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={confirmTransaction}
            style={{
              flex: 1,
              background: 'var(--gold)',
              color: 'var(--black)',
              border: 'none',
              padding: '15px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Confirm & Send
          </button>

          <button
            onClick={cancelTransaction}
            style={{
              flex: 1,
              background: 'transparent',
              color: '#ff4444',
              border: '1px solid #ff4444',
              padding: '15px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Cancel
          </button>
        </div>

        <div style={{
          background: 'rgba(255, 215, 0, 0.05)',
          padding: '15px',
          borderRadius: '8px',
          marginTop: '20px',
          border: '1px solid var(--gold)'
        }}>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '12px', lineHeight: '1.4' }}>
            <strong>Note:</strong> Please verify all details before confirming. Transactions cannot be reversed once confirmed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;
